import { supabase } from './supabase';
import { USE_MOCK_DATA } from './mockData';

export interface ContentItem {
  id?: string;
  client_id: string;
  title: string;
  content_type: 'post' | 'article' | 'image' | 'video' | 'document' | 'template' | 'other';
  description?: string;
  status?: 'draft' | 'pending_review' | 'approved' | 'published' | 'archived' | 'rejected';
  platform?: string;
  scheduled_date?: string;
  published_date?: string;
  author_id?: string;
  author_name?: string;
  tags?: string[];
  category_id?: string;
  is_template?: boolean;
  template_name?: string;
  metadata?: Record<string, any>;
  version_number?: number;
  total_versions?: number;
  comment_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ContentVersion {
  id?: string;
  content_id: string;
  version_number?: number;
  title: string;
  content_data: Record<string, any>;
  content_html?: string;
  content_text?: string;
  file_url?: string;
  thumbnail_url?: string;
  change_summary?: string;
  changed_by_id?: string;
  changed_by_name?: string;
  diff_from_previous?: Record<string, any>;
  is_major_version?: boolean;
  created_at?: string;
}

export interface ContentApproval {
  id?: string;
  content_id: string;
  version_id?: string;
  requested_by_id?: string;
  requested_by_name?: string;
  approver_id?: string;
  approver_name?: string;
  status?: 'pending' | 'approved' | 'rejected' | 'changes_requested';
  comments?: string;
  approved_at?: string;
  created_at?: string;
}

export interface ContentComment {
  id?: string;
  content_id: string;
  version_id?: string;
  parent_comment_id?: string;
  author_id?: string;
  author_name?: string;
  comment_text: string;
  comment_type?: 'general' | 'feedback' | 'suggestion' | 'issue' | 'approval';
  is_resolved?: boolean;
  created_at?: string;
}

export interface ContentActivity {
  id?: string;
  content_id: string;
  activity_type: string;
  activity_description?: string;
  actor_id?: string;
  actor_name?: string;
  changes?: Record<string, any>;
  created_at?: string;
}

class ContentRepositoryService {
  /**
   * Get all content items for a client
   */
  async getContentItems(clientId: string, filters?: {
    content_type?: string;
    status?: string;
    tags?: string[];
    search?: string;
  }) {
    if (USE_MOCK_DATA) {
      const { mockContentItems } = await import('./mockData');
      let filtered = mockContentItems.filter(c => c.client_id === clientId);

      if (filters?.content_type) {
        filtered = filtered.filter(c => c.content_type === filters.content_type);
      }
      if (filters?.status) {
        filtered = filtered.filter(c => c.status === filters.status);
      }
      if (filters?.search) {
        filtered = filtered.filter(c =>
          c.title.toLowerCase().includes(filters.search!.toLowerCase())
        );
      }

      return { success: true, data: filtered };
    }

    try {
      let query = supabase
        .from('content_with_version')
        .select('*')
        .eq('client_id', clientId);

      if (filters?.content_type) {
        query = query.eq('content_type', filters.content_type);
      }

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.tags && filters.tags.length > 0) {
        query = query.contains('tags', filters.tags);
      }

      if (filters?.search) {
        query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }

      const { data, error } = await query.order('updated_at', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting content:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get single content item with details
   */
  async getContentItem(contentId: string) {
    if (USE_MOCK_DATA) {
      const { mockContentItems } = await import('./mockData');
      const item = mockContentItems.find(c => c.id === contentId);
      return { success: true, data: item };
    }

    try {
      const { data, error } = await supabase
        .from('content_with_version')
        .select('*')
        .eq('id', contentId)
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting content item:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create new content item
   */
  async createContent(content: ContentItem) {
    if (USE_MOCK_DATA) {
      console.log('[ContentRepository] Mock mode: Would create content', content);
      return {
        success: true,
        data: {
          id: `content_${Date.now()}`,
          ...content,
          status: 'draft',
          created_at: new Date().toISOString()
        }
      };
    }

    try {
      // Create content record
      const { data: contentData, error: contentError } = await supabase
        .from('content_repository')
        .insert({
          client_id: content.client_id,
          title: content.title,
          content_type: content.content_type,
          description: content.description,
          status: content.status || 'draft',
          platform: content.platform,
          author_id: content.author_id,
          author_name: content.author_name,
          tags: content.tags || [],
          category_id: content.category_id,
          is_template: content.is_template || false,
          template_name: content.template_name,
          metadata: content.metadata || {}
        })
        .select()
        .single();

      if (contentError) throw contentError;

      return { success: true, data: contentData };
    } catch (error: any) {
      console.error('[ContentRepository] Error creating content:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update content item
   */
  async updateContent(contentId: string, updates: Partial<ContentItem>) {
    if (USE_MOCK_DATA) {
      console.log('[ContentRepository] Mock mode: Would update content', contentId, updates);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('content_repository')
        .update({
          title: updates.title,
          description: updates.description,
          status: updates.status,
          platform: updates.platform,
          scheduled_date: updates.scheduled_date,
          tags: updates.tags,
          category_id: updates.category_id,
          metadata: updates.metadata,
          updated_at: new Date().toISOString()
        })
        .eq('id', contentId);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('[ContentRepository] Error updating content:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete content item
   */
  async deleteContent(contentId: string) {
    if (USE_MOCK_DATA) {
      console.log('[ContentRepository] Mock mode: Would delete content', contentId);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('content_repository')
        .delete()
        .eq('id', contentId);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('[ContentRepository] Error deleting content:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create new version
   */
  async createVersion(version: ContentVersion) {
    if (USE_MOCK_DATA) {
      console.log('[ContentRepository] Mock mode: Would create version', version);
      return {
        success: true,
        data: {
          id: `version_${Date.now()}`,
          ...version,
          version_number: 2,
          created_at: new Date().toISOString()
        }
      };
    }

    try {
      const { data, error } = await supabase
        .from('content_versions')
        .insert({
          content_id: version.content_id,
          title: version.title,
          content_data: version.content_data,
          content_html: version.content_html,
          content_text: version.content_text,
          file_url: version.file_url,
          thumbnail_url: version.thumbnail_url,
          change_summary: version.change_summary,
          changed_by_id: version.changed_by_id,
          changed_by_name: version.changed_by_name,
          is_major_version: version.is_major_version || false
        })
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error: any) {
      console.error('[ContentRepository] Error creating version:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get version history
   */
  async getVersionHistory(contentId: string) {
    if (USE_MOCK_DATA) {
      const { mockContentVersions } = await import('./mockData');
      const versions = mockContentVersions.filter(v => v.content_id === contentId);
      return { success: true, data: versions };
    }

    try {
      const { data, error } = await supabase
        .from('content_versions')
        .select('*')
        .eq('content_id', contentId)
        .order('version_number', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting version history:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get specific version
   */
  async getVersion(versionId: string) {
    if (USE_MOCK_DATA) {
      const { mockContentVersions } = await import('./mockData');
      const version = mockContentVersions.find(v => v.id === versionId);
      return { success: true, data: version };
    }

    try {
      const { data, error } = await supabase
        .from('content_versions')
        .select('*')
        .eq('id', versionId)
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting version:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Compare two versions
   */
  async compareVersions(versionId1: string, versionId2: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: {
          differences: [
            { field: 'title', old: 'Old Title', new: 'New Title' },
            { field: 'content', old: 'Old content', new: 'Updated content' }
          ]
        }
      };
    }

    try {
      const [v1Result, v2Result] = await Promise.all([
        this.getVersion(versionId1),
        this.getVersion(versionId2)
      ]);

      if (!v1Result.success || !v2Result.success) {
        throw new Error('Failed to fetch versions');
      }

      const v1 = v1Result.data;
      const v2 = v2Result.data;

      const differences = [];

      if (v1.title !== v2.title) {
        differences.push({ field: 'title', old: v1.title, new: v2.title });
      }

      if (JSON.stringify(v1.content_data) !== JSON.stringify(v2.content_data)) {
        differences.push({ field: 'content', old: v1.content_data, new: v2.content_data });
      }

      return { success: true, data: { differences } };
    } catch (error: any) {
      console.error('[ContentRepository] Error comparing versions:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Restore version
   */
  async restoreVersion(contentId: string, versionId: string, restoredBy: string) {
    if (USE_MOCK_DATA) {
      console.log('[ContentRepository] Mock mode: Would restore version', versionId);
      return { success: true };
    }

    try {
      const versionResult = await this.getVersion(versionId);
      if (!versionResult.success) throw new Error('Version not found');

      const version = versionResult.data;

      // Create new version from old version
      await this.createVersion({
        content_id: contentId,
        title: version.title,
        content_data: version.content_data,
        content_html: version.content_html,
        content_text: version.content_text,
        file_url: version.file_url,
        thumbnail_url: version.thumbnail_url,
        change_summary: `Restored from version ${version.version_number}`,
        changed_by_name: restoredBy,
        is_major_version: true
      });

      return { success: true };
    } catch (error: any) {
      console.error('[ContentRepository] Error restoring version:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Request approval
   */
  async requestApproval(approval: ContentApproval) {
    if (USE_MOCK_DATA) {
      console.log('[ContentRepository] Mock mode: Would request approval', approval);
      return {
        success: true,
        data: { id: `approval_${Date.now()}`, ...approval, status: 'pending' }
      };
    }

    try {
      const { data, error } = await supabase
        .from('content_approvals')
        .insert({
          content_id: approval.content_id,
          version_id: approval.version_id,
          requested_by_id: approval.requested_by_id,
          requested_by_name: approval.requested_by_name,
          approver_id: approval.approver_id,
          approver_name: approval.approver_name,
          comments: approval.comments,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      // Update content status
      await this.updateContent(approval.content_id, { status: 'pending_review' });

      return { success: true, data };
    } catch (error: any) {
      console.error('[ContentRepository] Error requesting approval:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update approval status
   */
  async updateApproval(approvalId: string, status: string, comments?: string) {
    if (USE_MOCK_DATA) {
      console.log('[ContentRepository] Mock mode: Would update approval', approvalId, status);
      return { success: true };
    }

    try {
      const { data: approval, error: fetchError } = await supabase
        .from('content_approvals')
        .select('content_id')
        .eq('id', approvalId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('content_approvals')
        .update({
          status,
          comments,
          approved_at: status === 'approved' ? new Date().toISOString() : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', approvalId);

      if (error) throw error;

      // Update content status
      const contentStatus = status === 'approved' ? 'approved' : status === 'rejected' ? 'rejected' : 'draft';
      await this.updateContent(approval.content_id, { status: contentStatus });

      return { success: true };
    } catch (error: any) {
      console.error('[ContentRepository] Error updating approval:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get approvals for content
   */
  async getApprovals(contentId: string) {
    if (USE_MOCK_DATA) {
      const { mockContentApprovals } = await import('./mockData');
      const approvals = mockContentApprovals.filter(a => a.content_id === contentId);
      return { success: true, data: approvals };
    }

    try {
      const { data, error } = await supabase
        .from('content_approvals')
        .select('*')
        .eq('content_id', contentId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting approvals:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Add comment
   */
  async addComment(comment: ContentComment) {
    if (USE_MOCK_DATA) {
      console.log('[ContentRepository] Mock mode: Would add comment', comment);
      return {
        success: true,
        data: { id: `comment_${Date.now()}`, ...comment, created_at: new Date().toISOString() }
      };
    }

    try {
      const { data, error } = await supabase
        .from('content_comments')
        .insert({
          content_id: comment.content_id,
          version_id: comment.version_id,
          parent_comment_id: comment.parent_comment_id,
          author_id: comment.author_id,
          author_name: comment.author_name,
          comment_text: comment.comment_text,
          comment_type: comment.comment_type || 'general'
        })
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error: any) {
      console.error('[ContentRepository] Error adding comment:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get comments
   */
  async getComments(contentId: string) {
    if (USE_MOCK_DATA) {
      const { mockContentComments } = await import('./mockData');
      const comments = mockContentComments.filter(c => c.content_id === contentId);
      return { success: true, data: comments };
    }

    try {
      const { data, error } = await supabase
        .from('content_comments')
        .select('*')
        .eq('content_id', contentId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting comments:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get activity log
   */
  async getActivityLog(contentId: string) {
    if (USE_MOCK_DATA) {
      const { mockContentActivity } = await import('./mockData');
      const activities = mockContentActivity.filter(a => a.content_id === contentId);
      return { success: true, data: activities };
    }

    try {
      const { data, error } = await supabase
        .from('content_activity_log')
        .select('*')
        .eq('content_id', contentId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting activity log:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get tags for client
   */
  async getTags(clientId: string) {
    if (USE_MOCK_DATA) {
      const { mockContentTags } = await import('./mockData');
      return { success: true, data: mockContentTags };
    }

    try {
      const { data, error } = await supabase
        .from('content_tags')
        .select('*')
        .eq('client_id', clientId)
        .order('usage_count', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting tags:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get categories for client
   */
  async getCategories(clientId: string) {
    if (USE_MOCK_DATA) {
      const { mockContentCategories } = await import('./mockData');
      return { success: true, data: mockContentCategories };
    }

    try {
      const { data, error } = await supabase
        .from('content_categories')
        .select('*')
        .eq('client_id', clientId)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting categories:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get content statistics
   */
  async getStatistics(clientId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: {
          total_content: 45,
          by_status: { draft: 12, pending_review: 5, approved: 18, published: 10 },
          by_type: { post: 25, article: 10, image: 5, video: 3, document: 2 },
          total_versions: 156,
          avg_versions_per_content: 3.5
        }
      };
    }

    try {
      const { data: content, error } = await supabase
        .from('content_repository')
        .select('id, status, content_type')
        .eq('client_id', clientId);

      if (error) throw error;

      const stats = {
        total_content: content?.length || 0,
        by_status: {} as Record<string, number>,
        by_type: {} as Record<string, number>,
        total_versions: 0,
        avg_versions_per_content: 0
      };

      content?.forEach(item => {
        stats.by_status[item.status] = (stats.by_status[item.status] || 0) + 1;
        stats.by_type[item.content_type] = (stats.by_type[item.content_type] || 0) + 1;
      });

      return { success: true, data: stats };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting statistics:', error);
      return { success: false, error: error.message, data: null };
    }
  }
  /**
   * Get content by client and date range
   */
  async getContentByDateRange(
    clientId: string,
    startDate: string,
    endDate: string,
    filters?: {
      content_type?: string;
      status?: string;
    }
  ) {
    if (USE_MOCK_DATA) {
      const { mockContentItems } = await import('./mockData');
      let filtered = mockContentItems.filter(c => {
        const created = new Date(c.created_at);
        const start = new Date(startDate);
        const end = new Date(endDate);
        return c.client_id === clientId && created >= start && created <= end;
      });

      if (filters?.content_type) {
        filtered = filtered.filter(c => c.content_type === filters.content_type);
      }
      if (filters?.status) {
        filtered = filtered.filter(c => c.status === filters.status);
      }

      return { success: true, data: filtered };
    }

    try {
      const { data, error } = await supabase.rpc('get_client_content_by_date', {
        p_client_id: clientId,
        p_start_date: startDate,
        p_end_date: endDate,
        p_content_type: filters?.content_type || null,
        p_status: filters?.status || null
      });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting content by date:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Download content
   */
  async downloadContent(
    contentId: string,
    clientId: string,
    userId?: string,
    userName?: string,
    fileFormat?: string
  ) {
    if (USE_MOCK_DATA) {
      console.log('[ContentRepository] Mock mode: Would download content', contentId);
      return {
        success: true,
        data: {
          download_id: `download_${Date.now()}`,
          file_url: 'https://example.com/mock-file.pdf'
        }
      };
    }

    try {
      const { data, error } = await supabase.rpc('record_content_download', {
        p_content_id: contentId,
        p_client_id: clientId,
        p_user_id: userId || null,
        p_user_name: userName || null,
        p_download_type: 'download',
        p_file_format: fileFormat || null
      });

      if (error) throw error;

      // Get the file URL
      const { data: content } = await this.getContentItem(contentId);

      return {
        success: true,
        data: {
          download_id: data,
          file_url: content?.file_url
        }
      };
    } catch (error: any) {
      console.error('[ContentRepository] Error downloading content:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get retention policy configuration
   */
  async getRetentionPolicy() {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: {
          default_retention_days: 365,
          by_content_type: {
            post: 180,
            article: 365,
            image: 730,
            video: 365,
            document: 1095,
            template: -1
          }
        }
      };
    }

    try {
      const { data, error } = await supabase
        .from('global_configuration')
        .select('config_value')
        .eq('config_key', 'content_retention_policy')
        .eq('is_active', true)
        .single();

      if (error) throw error;

      return { success: true, data: data.config_value };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting retention policy:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Set client-specific retention override
   */
  async setClientRetentionOverride(
    clientId: string,
    contentType: string | null,
    retentionDays: number,
    reason?: string,
    setBy?: string
  ) {
    if (USE_MOCK_DATA) {
      console.log('[ContentRepository] Mock mode: Would set retention override', {
        clientId,
        contentType,
        retentionDays
      });
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('client_retention_overrides')
        .upsert({
          client_id: clientId,
          content_type: contentType,
          retention_days: retentionDays,
          reason,
          set_by_name: setBy,
          is_active: true,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('[ContentRepository] Error setting retention override:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get download history
   */
  async getDownloadHistory(clientId: string, limit: number = 50) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'dl_1',
            content_id: 'content_1',
            user_name: 'John Doe',
            download_type: 'download',
            downloaded_at: new Date().toISOString()
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('content_downloads')
        .select('*')
        .eq('client_id', clientId)
        .order('downloaded_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting download history:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Auto-archive expired content (admin function)
   */
  async autoArchiveExpired() {
    if (USE_MOCK_DATA) {
      console.log('[ContentRepository] Mock mode: Would auto-archive expired content');
      return { success: true, data: { archived_count: 5 } };
    }

    try {
      const { data, error } = await supabase.rpc('auto_archive_expired_content');

      if (error) throw error;

      return { success: true, data: { archived_count: data } };
    } catch (error: any) {
      console.error('[ContentRepository] Error auto-archiving:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get content with retention info
   */
  async getContentWithRetention(clientId: string) {
    if (USE_MOCK_DATA) {
      const { mockContentItems } = await import('./mockData');
      const items = mockContentItems
        .filter(c => c.client_id === clientId)
        .map(c => ({
          ...c,
          retention_status: 'Active',
          days_until_expiry: 180,
          is_downloadable: true
        }));
      return { success: true, data: items };
    }

    try {
      const { data, error } = await supabase
        .from('content_with_retention')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[ContentRepository] Error getting content with retention:', error);
      return { success: false, error: error.message, data: [] };
    }
  }
}

export const contentRepositoryService = new ContentRepositoryService();
